import { inject, Injectable } from '@angular/core';
import { FirestoreService } from '../../firestore.service';
import { BehaviorSubject, map } from 'rxjs';
import { UpdateWorkout, Workout } from './workouts-collection.models';
import { FIRESTORE_COLLECTIONS } from '../../firestore.constants';
import { chunk } from '../../../../shared/utils/array.utils';
import { CountersCollectionService } from '../counters/counters-collection.service';
import { COUNTER_DOCUMENT_IDS } from '../counters/counters-collection.constants';
import { ExercisesCollectionService } from '../exercises/exercises-collection.service';
import { documentId, Timestamp } from '@firebase/firestore';
import { omitFirestoreFields } from '../../firestore.utils';
import { AuthService } from '../../../auth/auth.service';
import { QueryFilter } from '../../firestore.models';

@Injectable({
  providedIn: 'root',
})
export class WorkoutsCollectionService {
  private _fetchedAll = false;
  private readonly _workoutsMapSubject = new BehaviorSubject(new Map<string, Workout>());
  private readonly _firestoreService = inject(FirestoreService);
  private readonly _countersCollectionService = inject(CountersCollectionService);
  private readonly _exercisesCollectionService = inject(ExercisesCollectionService);
  private readonly _authService = inject(AuthService);

  public readonly workouts$ = this._workoutsMapSubject
    .asObservable()
    .pipe(map(() => this.workouts));

  private get _workoutsMap(): Map<string, Workout> {
    return this._workoutsMapSubject.value;
  }

  private set _workoutsMap(value: Map<string, Workout>) {
    this._workoutsMapSubject.next(value);
  }

  public get workouts(): Workout[] {
    return Array.from(this._workoutsMap.values());
  }

  public async isExerciseInUse(id: string): Promise<boolean> {
    return this._firestoreService.isExists<Workout>({
      collectionName: FIRESTORE_COLLECTIONS.WORKOUTS,
      filters: [{ field: 'exerciseIds', operator: 'array-contains-any', value: [id] }],
    });
  }

  public async isWorkoutNameExists(name: string, docId?: string): Promise<boolean> {
    const filters: QueryFilter<Workout>[] = [
      { field: 'name', operator: '==', value: name },
      { field: 'isDeleted', operator: '==', value: false },
    ];

    if (docId) {
      filters.push({ field: documentId(), operator: '!=', value: docId });
    }

    return this._firestoreService.isExists<Workout>({
      collectionName: FIRESTORE_COLLECTIONS.WORKOUTS,
      filters,
    })
  }

  public getWorkoutFromCache(documentId: string): Workout | undefined {
    return this._workoutsMap.get(documentId);
  }

  public async getWorkout(documentId: string): Promise<Workout | undefined> {
    const workoutsMap = this._workoutsMap;
    if (workoutsMap.has(documentId)) return workoutsMap.get(documentId);

    const workout = await this._firestoreService.getDocument<Workout>({
      collectionName: FIRESTORE_COLLECTIONS.WORKOUTS,
      documentId,
    });

    if (!workout) return undefined;

    // Prefetch the first exercise to be used as default thumbnail
    const workoutPlan = workout.workoutPlan;
    if (workoutPlan.length > 0) {
      const exercises = workoutPlan[0].exercises;
      if (exercises.length > 0) {
        await this._exercisesCollectionService.getExercise(exercises[0].id);
      }
    }

    const exercisesIds = new Set<string>();
    workout.workoutPlan.forEach((workoutPlan) => {
      workoutPlan.exercises.forEach((exercise) => exercisesIds.add(exercise.id));
    });

    const exerciseIdsChunks = chunk(Array.from(exercisesIds.values()), 30);
    for (const exerciseIdsChunk of chunk(exerciseIdsChunks, 3)) {
      await Promise.all(
        exerciseIdsChunk.map((exerciseIds) =>
          this._exercisesCollectionService.getExercisesByIds(exerciseIds),
        ),
      );
    }

    this._workoutsMap = this._workoutsMap.set(workout.id!, workout);
    return workout;
  }

  public async getWorkouts(): Promise<Workout[]> {
    const workoutsMap = this._workoutsMap;
    if (this._fetchedAll) return this.workouts;

    const workouts = await this._firestoreService.getDocuments<Workout>({
      collectionName: FIRESTORE_COLLECTIONS.WORKOUTS,
      filters: [{ field: 'isDeleted', operator: '==', value: false }],
    });

    // Prefetch the first exercise to be used as default thumbnail
    const workoutsWithoutThumbnail = workouts.filter(workout => !workout.thumbnailUrl);
    const exerciseIds = [...new Set(
      workoutsWithoutThumbnail.reduce<string[]>((data, workout) => {
        if (workout.workoutPlan.length > 0) {
          const workoutPlan = workout.workoutPlan[0];
          if (workoutPlan.exercises.length > 0) {
            data.push(workoutPlan.exercises[0].id);
          }
        }
        return data;
      }, [])
    )];

    const exerciseIdsChunks = chunk(exerciseIds, 30);
    for (const exerciseIdsChunk of chunk(exerciseIdsChunks, 3)) {
      await Promise.all(
        exerciseIdsChunk.map((exerciseIds) =>
          this._exercisesCollectionService.getExercisesByIds(exerciseIds),
        ),
      );
    }

    workouts.forEach((workout) => workoutsMap.set(workout.id!, workout));
    this._workoutsMap = workoutsMap;
    this._fetchedAll = true;

    return workouts;
  }

  public async createWorkout(workout: Workout): Promise<Workout> {
    const counter = await this._countersCollectionService.incrementCount(
      COUNTER_DOCUMENT_IDS.WORKOUTS,
    );

    const newWorkout = await this._firestoreService.createDocument<Workout>({
      collectionName: FIRESTORE_COLLECTIONS.WORKOUTS,
      data: {
        ...workout,
        orderNo: counter.count,
        proWorkoutAccess: false,
        status: true,
        isDeleted: false,
        deletedBy: null,
        dateTimeDeleted: null,
        dateCreated: Timestamp.now(),
      },
    });

    this._workoutsMap = this._workoutsMap.set(workout.id!, newWorkout);
    return newWorkout;
  }

  public async updateWorkout(id: string, workout: UpdateWorkout): Promise<Workout> {
    const workoutsMap = this._workoutsMap;
    await this._firestoreService.updateDocument<Workout>({
      collectionName: FIRESTORE_COLLECTIONS.WORKOUTS,
      id: id,
      data: { ...workout },
    });

    const updatedWorkout = { ...workoutsMap.get(id)!, ...workout };
    workoutsMap.set(id, updatedWorkout);
    this._workoutsMapSubject.next(workoutsMap);
    return updatedWorkout;
  }

  public async deleteWorkout(id: string): Promise<void> {
    await this._firestoreService.updateDocument<Workout>({
      collectionName: FIRESTORE_COLLECTIONS.WORKOUTS,
      id,
      data: {
        isDeleted: true,
        deletedBy: this._authService.userId,
        dateTimeDeleted: Timestamp.now()
      }
    });

    const workoutsMap = this._workoutsMap;
    workoutsMap.delete(id);
    this._workoutsMapSubject.next(workoutsMap);
  }

  public async cloneWorkout(workout: Workout): Promise<Workout> {
    const clonedWorkout = { ...omitFirestoreFields(workout), name: `${workout.name} (Copy)` };

    const counter = await this._countersCollectionService.incrementCount(
      COUNTER_DOCUMENT_IDS.WORKOUTS,
    );

    clonedWorkout.orderNo = counter.count;
    return this.createWorkout(clonedWorkout);
  }

  public async reorderWorkouts(workouts: Workout[]): Promise<void> {
    const workoutsMap = this._workoutsMap;
    const workoutChunks = chunk(workouts, 450);
    for (const workoutChunk of workoutChunks) {
      const batch = this._firestoreService.batch();
      workoutChunk.forEach((workout) => {
        const workoutRef = this._firestoreService.documentRef(
          FIRESTORE_COLLECTIONS.WORKOUTS,
          workout.id!,
        );
        batch.update(workoutRef, { orderNo: workout.orderNo } as Pick<Workout, 'orderNo'>);

        workoutsMap.set(workout.id!, {
          ...workoutsMap.get(workout.id!)!,
          orderNo: workout.orderNo,
        });
      });

      await batch.commit();
    }

    this._workoutsMapSubject.next(workoutsMap);
  }
}
